import React, { useEffect } from 'react';
import { Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useProfile } from '@/hooks/useProfile';
import { useSupabaseStore } from '@/store/SupabaseStore';
import { useAppStore } from '@/store';
import LoadingScreen from '@/components/ui/loading-screen';
import AuthPage from '@/pages/Auth';
import ProtectedRoute from '@/components/auth/ProtectedRoute';
import Index from '@/pages/Index';
import Chat from '@/pages/Chat';
import LobbyPage from '@/pages/Lobby';
import Projects from '@/pages/Projects';
import ProjectDetail from '@/pages/ProjectDetail';
import Canvas from '@/pages/Canvas';
import Code from '@/pages/Code';
import KnowledgeBase from '@/pages/KnowledgeBase';
import MCPServices from '@/pages/MCPServices';
import Users from '@/pages/Users';
import Settings from '@/pages/Settings';
import Profile from '@/pages/Profile';
import AgentMarketplace from '@/pages/agents/AgentMarketplace';
import CustomAgent from '@/pages/agents/CustomAgent';
import ProfessionalAgents from '@/pages/agents/ProfessionalAgents';
import ScientificAgents from '@/pages/agents/ScientificAgents';
import DataPolicy from '@/pages/Data';
import Eula from '@/pages/Eula';
import About from '@/pages/About';
import News from '@/pages/News';
import NewsDetail from '@/pages/NewsDetail';
import Purchase from '@/pages/Purchase';
import ProductDetails from '@/pages/ProductDetails';
import AICredits from '@/pages/AICredits';
import Community from '@/pages/Community';
import BugReports from '@/pages/BugReports';
import Docs from '@/pages/Docs';

import Downloads from '@/pages/Downloads';
import NotFound from '@/pages/NotFound';
import { trackEvent } from '@/lib/analytics';
import { registerServiceWorker } from '@/integrations/supabase/client';
import ChangelogPage from '@/pages/Changelog';
import UseAgent from '@/pages/agents/UseAgent';
import LandingPage from '@/pages/LandingPage';
import WelcomePage from '@/pages/WelcomePage';
import Research from '@/pages/Research';
import Partnership from '@/pages/Partnership';
import { initGA, trackPageView } from '@/lib/ga';
import { loadRewardfulScript } from '@/utils/rewardful';
import CompleteRegistrationPage from '@/pages/CompleteRegistration';
import Privacy from '@/pages/Privacy';
import MaplePT from '@/pages/MaplePT';
import MapleOSStart from '@/pages/MapleOSStart';
import AccessControl from '@/pages/AccessControl';
import { useToast } from '@/components/ui/use-toast';

import MainLayout from '@/components/layout/MainLayout';
import IntelligenceXP from '@/pages/IntelligenceXP';
import WebSettings from '@/pages/WebSettings';
import WebProfile from '@/pages/WebProfile';
import Admin from '@/pages/Admin';
import MapleMarket from '@/pages/MapleMarket';
import MapleMarketAppDetail from '@/pages/MapleMarketAppDetail';
import MapleMarketLibrary from '@/pages/MapleMarketLibrary';

export default function AppInitializer() {
  const { loading: authLoading, user } = useAuth();
  const { profile } = useProfile();
  const { fetchChats, fetchModels, fetchProjects } = useSupabaseStore();
  const { textSize, saveHistory, allowAnalytics } = useAppStore();
  const location = useLocation();
  const { toast } = useToast();

  const isElectron = typeof navigator !== 'undefined' && navigator.userAgent.toLowerCase().includes('electron');

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const isEmbeddedMapleOsChat =
      location.pathname.startsWith('/chat/') && params.get('embeddedInMapleOS') === 'true';

    if (isEmbeddedMapleOsChat) {
      delete document.documentElement.dataset.brand;

      const forcedTheme = params.get('theme');
      if (forcedTheme === 'dark') {
        document.documentElement.classList.add('dark');
      } else if (forcedTheme === 'light') {
        document.documentElement.classList.remove('dark');
      }

      return;
    }

    const isOsUiRoute =
      location.pathname.startsWith('/desktop/start') ||
      location.pathname.startsWith('/start') ||
      location.pathname.startsWith('/os');

    if (isOsUiRoute) {
      delete document.documentElement.dataset.brand;
    } else {
      document.documentElement.dataset.brand = 'mapleos';
    }
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (allowAnalytics) {
      initGA();
      // Dynamically load Rewardful only when analytics are allowed
      loadRewardfulScript('a6af9b');
    }
  }, [allowAnalytics]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const via = params.get("via");
  
    if (via) {
      localStorage.setItem("rewardful_referral", via);
    } else {
      const savedVia = localStorage.getItem("rewardful_referral");
      if (savedVia) {
        const newUrl = new URL(window.location.href);
        newUrl.searchParams.set("via", savedVia);
        window.history.replaceState({}, "", newUrl.toString());
      }
    }
  }, []);

  // Auto-check for updates via Service Worker on mount, focus, and when tab becomes visible
  useEffect(() => {
    if (isElectron) return; // Electron updates handled separately

    let inFlight = false;
    let lastCheckAt = 0;
    const MIN_CHECK_INTERVAL_MS = 60_000;

    const applyUpdate = () => {
      if (!('serviceWorker' in navigator)) {
        window.location.reload();
        return;
      }
      const controller = navigator.serviceWorker.controller;
      if (!controller) {
        window.location.reload();
        return;
      }
      toast({ title: 'Updating…', description: 'A new version is ready. Applying update now.' });
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        window.location.reload();
      });
      controller.postMessage({ type: 'SKIP_WAITING' });
    };

    const checkForSwUpdate = async () => {
      const now = Date.now();
      if (inFlight) return;
      if (now - lastCheckAt < MIN_CHECK_INTERVAL_MS) return;
      lastCheckAt = now;
      inFlight = true;

      try {
        if (!('serviceWorker' in navigator)) return;
        const registration = await navigator.serviceWorker.getRegistration();
        await registration?.update();

        const controller = navigator.serviceWorker.controller;
        if (!controller) return;

        const mc = new MessageChannel();
        mc.port1.onmessage = (event) => {
          const hasUpdate = event?.data?.hasUpdate === true;
          if (hasUpdate) {
            applyUpdate();
          }
          try {
            mc.port1.close();
            mc.port2.close();
          } catch {
          }
        };

        controller.postMessage({ type: 'CHECK_FOR_UPDATE' }, [mc.port2]);
      } finally {
        inFlight = false;
      }
    };

    checkForSwUpdate();

    const onFocus = () => checkForSwUpdate();
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkForSwUpdate();
      }
    };

    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [isElectron, toast, location.pathname]);

  useEffect(() => {
    if (!authLoading && user) {
      fetchChats(user.id);
      fetchModels();
      fetchProjects(user.id);
    }
  }, [authLoading, user, fetchChats, fetchModels, fetchProjects]);

  useEffect(() => {
    trackEvent('page_view');
    trackPageView(location.pathname + location.search);
  }, [location]);

  const isLoading = authLoading || (user && !profile);

  if (isLoading) {
    return <LoadingScreen />;
  }

  return (
    <Routes>
      <Route path="/auth" element={<AuthPage />} />
      <Route path="/complete-registration" element={<CompleteRegistrationPage />} />

      <Route
        path="/"
        element={
          isElectron ? (
            <Navigate to="/auth" replace />
          ) : (
            <LandingPage />
          )
        }
      />

      <Route path="/market" element={<MapleMarket />} />
      <Route path="/market/library" element={<MapleMarketLibrary />} />
      <Route path="/market/library/:appId" element={<MapleMarketLibrary />} />
      <Route path="/market/app/:appId" element={<MapleMarketAppDetail />} />
      <Route path="/market/help" element={<Navigate to="/market?page=help" replace />} />
      <Route path="/market/contact" element={<Navigate to="/market?page=contact" replace />} />
      <Route path="/market/refund-policy" element={<Navigate to="/market?page=refund-policy" replace />} />
      <Route path="/market/developers" element={<Navigate to="/market?page=developers" replace />} />
      <Route path="/market/developers/api" element={<Navigate to="/market?page=developers-api" replace />} />
      <Route path="/market/submit-app" element={<Navigate to="/market?page=submit-app" replace />} />
      <Route path="/market/legal/eula" element={<Navigate to="/market?page=legal-eula" replace />} />
      <Route path="/market/legal/privacy" element={<Navigate to="/market?page=legal-privacy" replace />} />
      <Route path="/market/legal/data" element={<Navigate to="/market?page=legal-data" replace />} />

      <Route
        path="/chats"
        element={
          <ProtectedRoute>
            <Index />
          </ProtectedRoute>
        }
      />

      <Route path="/chat/:chatId" element={
        <ProtectedRoute><Chat /></ProtectedRoute>
      } />
      <Route path="/lobby" element={
        <ProtectedRoute><LobbyPage /></ProtectedRoute>
      } />
      <Route path="/lobby/:channelSlug" element={
        <ProtectedRoute><LobbyPage /></ProtectedRoute>
      } />
      <Route path="/projects" element={
        <ProtectedRoute><Projects /></ProtectedRoute>
      } />
      <Route path="/projects/:id" element={
        <ProtectedRoute><ProjectDetail /></ProtectedRoute>
      } />
      <Route path="/canvas" element={
        <ProtectedRoute><Canvas /></ProtectedRoute>
      } />
      <Route path="/code" element={
        <ProtectedRoute><Code /></ProtectedRoute>
      } />
      <Route path="/knowledge" element={
        <ProtectedRoute><KnowledgeBase /></ProtectedRoute>
      } />
      <Route path="/kb" element={
        <ProtectedRoute><Navigate to="/knowledge" replace /></ProtectedRoute>
      } />

      <Route path="/mcp" element={
        <ProtectedRoute><MCPServices /></ProtectedRoute>
      } />
      <Route path="/users" element={
        <ProtectedRoute><Users /></ProtectedRoute>
      } />
      <Route path="/settings" element={
        <ProtectedRoute><Settings /></ProtectedRoute>
      } />
      <Route path="/profile" element={
        <ProtectedRoute>
          <Profile />
        </ProtectedRoute>
      } />

      <Route path="/agents" element={
        <ProtectedRoute><AgentMarketplace /></ProtectedRoute>
      } />
      <Route path="/agents/custom" element={
        <ProtectedRoute><CustomAgent /></ProtectedRoute>
      } />
      <Route path="/agents/professional" element={
        <ProtectedRoute><ProfessionalAgents /></ProtectedRoute>
      } />
      <Route path="/agents/scientific" element={
        <ProtectedRoute><ScientificAgents /></ProtectedRoute>
      } />
      <Route path="/agents/use" element={
        <ProtectedRoute><UseAgent /></ProtectedRoute>
      } />
      <Route path="/data-policy" element={<DataPolicy />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/eula" element={<Eula />} />
      <Route path="/about" element={<About />} />
      <Route path="/news" element={<News />} />
      <Route path="/news/:slug" element={<NewsDetail />} />
      <Route path="/purchase" element={<Purchase />} />
      <Route path="/products/:productId" element={<ProductDetails />} />
      <Route path="/credits" element={<AICredits />} />
      <Route path="/community" element={<Navigate to="/forums" replace />} />

      <Route path="/forums" element={<Community />} />
      <Route path="/bugs" element={<BugReports />} />
      <Route path="/bug-reports" element={<Navigate to="/bugs" replace />} />
      <Route path="/downloads" element={<Downloads />} />
      <Route path="/docs" element={<Docs />} />
      <Route path="/changelog" element={<ChangelogPage />} />
      <Route path="/welcome" element={<WelcomePage />} />
      <Route path="/research" element={<Research />} />
      <Route path="/partnership" element={<Partnership />} />
      <Route path="/maplept" element={<MaplePT />} />
      <Route path="/desktop/start/*" element={<MapleOSStart />} />
      <Route path="/start/*" element={<MapleOSStart />} />
      <Route path="/access-control" element={<AccessControl />} />
      <Route
        path="/web-settings"
        element={
          <MainLayout>
            <WebSettings />
          </MainLayout>
        }
      />
      <Route path="/admin" element={
        <ProtectedRoute><Admin /></ProtectedRoute>
      } />
      <Route
        path="/web-profile"
        element={
          <MainLayout>
            <WebProfile />
          </MainLayout>
        }
      />
      <Route path="*" element={<NotFound />} />

    </Routes>
  );
}